<script setup lang="ts">
import {
    onMounted,
    ref,
    computed,
    defineProps,
    defineEmits,
    withDefaults,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { CheckSensitiveWordService } from '@/services/common/check-sensitive-word';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';

const checkSensitiveWordService = injectService<CheckSensitiveWordService>(
    CheckSensitiveWordService
);

const MAX_LENGTH = 500;

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        isLoadingSubmit?: boolean;
        defaultText?: string;
        submitLabel?: string;
        type?: 'original' | 'translated';
    }>(),
    {
        isLoadingSubmit: false,
        defaultText: '',
        submitLabel: '',
        type: 'translated',
    }
);

const text = ref(props.defaultText);
const textareaRef = ref(null);
const isCheckingSensitiveWord = ref<boolean>(false);

const emit = defineEmits<{
    (event: 'cancel'): void;
    (event: 'submit', newText: string): void;
}>();

const isOriginal = computed(() => props.type === 'original');

function adjustHeight() {
    textareaRef.value.style.height = 'auto'; // 重置高度
    textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`; // 设置新高度
}

onMounted(() => {
    adjustHeight();
});

async function onSubmit() {
    isCheckingSensitiveWord.value = true;
    const res = await checkSensitiveWordService.checkSensitiveWord(text.value);
    isCheckingSensitiveWord.value = false;
    if (res) return;

    emit('submit', text.value);
}

function onCancel() {
    emit('cancel');
}
</script>

<template>
    <div class="rounded-lg border border-primary p-3">
        <textarea
            ref="textareaRef"
            v-model.trim="text"
            class="min-h-8 w-full resize-none overflow-y-auto pb-10 text-base leading-8 text-black outline-none"
            type="text"
            :maxlength="isOriginal && MAX_LENGTH"
            @input="adjustHeight"
        />

        <div class="flex items-center justify-end gap-2">
            <div
                v-if="isOriginal"
                class="text-xs"
                :class="[
                    text.length >= MAX_LENGTH
                        ? 'text-red-500'
                        : 'text-grey-90',
                ]"
            >
                {{ text.length }}/{{ MAX_LENGTH }}
            </div>
            <button class="th-btn th-btn-secondary" @click="onCancel">
                {{ t('aiTranslation.cancel') }}
            </button>
            <button
                class="th-btn"
                :disabled="
                    text.length === 0 ||
                        (isOriginal && text.length > MAX_LENGTH)
                "
                @click="onSubmit"
            >
                <span
                    v-if="isCheckingSensitiveWord || isLoadingSubmit"
                    class="th-loading th-loading-spinner th-loading-sm"
                />
                <span v-else>
                    {{
                        submitLabel || t('aiDisclosureGlobal.globalBtn.submit')
                    }}
                </span>
            </button>
        </div>
    </div>
</template>
